import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { AmountField } from "@/components/ui/AmountField";
import { ModalShell } from "@/components/ui/ModalShell";
import { FormField, ModalActions, TextArea, TextInput } from "@/components/ui/primitives";
import { amountToInput } from "@/utils/amountInput";
import "../savings-form.css";

interface SavingData {
  name: string;
  description?: string;
  initial_amount: number;
  target_amount: number;
  expected_date?: string;
}

interface Saving {
  id: number;
  name: string;
  description?: string;
  initial_amount: number;
  target_amount: number;
  expected_date?: string;
}

interface NewSavingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (savingData: SavingData, savingId?: number) => void;
  saving?: Saving | null; // Optional saving for edit mode
}

const emptyFormData: SavingData = {
  name: '',
  description: '',
  initial_amount: 0,
  target_amount: 0,
  expected_date: ''
};

export const NewSavingModal = ({ isOpen, onClose, onSubmit, saving }: NewSavingModalProps) => {
  const { t } = useTranslation(["savings", "budget", "common", "installments"]);
  const [formData, setFormData] = useState<SavingData>(emptyFormData);
  // Amount inputs keep the typed text ("12.", "0.5") apart from the numeric payload.
  const [amountInputs, setAmountInputs] = useState({ initial_amount: '', target_amount: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!saving;

  // Populate form when editing
  useEffect(() => {
    if (saving && isOpen) {
      setFormData({
        name: saving.name || '',
        description: saving.description || '',
        initial_amount: saving.initial_amount || 0,
        target_amount: saving.target_amount || 0,
        expected_date: saving.expected_date || ''
      });
      setAmountInputs({
        initial_amount: amountToInput(saving.initial_amount),
        target_amount: amountToInput(saving.target_amount),
      });
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData(emptyFormData);
      setAmountInputs({ initial_amount: '', target_amount: '' });
      setIsSubmitting(false);
    }
  }, [saving, isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, isSubmitting, onClose]);

  const handleInputChange = (field: keyof SavingData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAmountChange = (field: 'initial_amount' | 'target_amount', value: string) => {
    // Allow empty string, numbers, and decimal points
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const numValue = value === '' ? 0 : parseFloat(value);
      setFormData(prev => ({ ...prev, [field]: numValue }));
      setAmountInputs(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData, saving?.id);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name.trim() !== '' && formData.target_amount > 0;
  const optionalHint = <span className="app-form-required">{t("common.labels.optional")}</span>;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="saving-modal-title"
      title={isEditMode ? t("savings.editGoal") : t("savings.createGoal")}
      subtitle={isEditMode ? t("savings.modalSubtitle") : t("savings.newGoalSubtitle", { defaultValue: "Ahorra para algo concreto" })}
      onSubmit={handleSubmit}
      mobileSheet
      variant="creation"
      maxWidthClassName="!w-[min(100%,560px)] !max-w-[560px]"
      panelClassName="account-modal-panel saving-modal-panel !h-fit"
      disableClose={isSubmitting}
      footer={
        <ModalActions
          cancelLabel={t("common.actions.cancel")}
          submitLabel={isEditMode ? t("installments.saveChanges") : t("savings.createGoalAction")}
          submittingLabel={t("common.states.saving")}
          onCancel={onClose}
          isSubmitting={isSubmitting}
          isSubmitDisabled={!isFormValid}
        />
      }
    >
      <div className="saving-form">
        <p className="app-domain-sidebar-label saving-form-section">{t("budget.principalSection")}</p>
        <FormField label={t("common.labels.name")} htmlFor="saving-name" required>
          <TextInput
            id="saving-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder={t("savings.savingNamePlaceholder")}
            required
            disabled={isSubmitting}
          />
        </FormField>

        <FormField label={t("savings.targetAmount")} htmlFor="target-amount" required>
          <AmountField
            id="target-amount"
            value={amountInputs.target_amount}
            onChange={(value) => handleAmountChange('target_amount', value)}
            centsLabel={t("budget.amountCents")}
            required
            disabled={isSubmitting}
          />
        </FormField>

        <FormField label={<>{t("savings.targetDate")}{optionalHint}</>} htmlFor="expected-date">
          <TextInput
            id="expected-date"
            type="date"
            value={formData.expected_date || ''}
            onChange={(e) => handleInputChange('expected_date', e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>

        <p className="app-domain-sidebar-label saving-form-section saving-form-section-extra">
          {t("savings.optionalSection", { defaultValue: "Opcional" })}
        </p>

        <FormField label={<>{t("savings.initialAmount")}{optionalHint}</>} htmlFor="initial-amount">
          <TextInput
            id="initial-amount"
            type="text"
            inputMode="decimal"
            value={amountInputs.initial_amount}
            onChange={(e) => handleAmountChange('initial_amount', e.target.value)}
            placeholder="0.00"
            disabled={isSubmitting}
          />
        </FormField>

        <FormField label={<>{t("common.labels.description")}{optionalHint}</>} htmlFor="saving-description">
          <TextArea
            id="saving-description"
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder={t("savings.descriptionPlaceholder")}
            rows={3}
            className="resize-none"
            disabled={isSubmitting}
          />
        </FormField>
      </div>
    </ModalShell>
  );
};
